<template>
  <main class="feedback-settings settings-card">
    <div class="feedback-heading">
      <div>
        <div class="section-kicker">Podpora používateľov</div>
        <h2>Feedback používateľov</h2>
        <p>
          Popis situácie, stránka, hypotéka a screenshot sú spolu v jednom zázname.
        </p>
      </div>
      <button
        class="secondary-button"
        type="button"
        :disabled="loading"
        @click="loadItems"
      >
        {{ loading ? "Obnovujem..." : "Obnoviť" }}
      </button>
    </div>

    <div class="feedback-stats">
      <div class="stat-card stat-card--all">
        <strong>{{ total }}</strong>
        <span>všetkých hlásení</span>
      </div>
      <div class="stat-card stat-card--new">
        <strong>{{ statusCount("new") }}</strong>
        <span>nových</span>
      </div>
      <div class="stat-card stat-card--progress">
        <strong>{{ statusCount("in_progress") }}</strong>
        <span>rieši sa</span>
      </div>
      <div class="stat-card stat-card--resolved">
        <strong>{{ statusCount("resolved") }}</strong>
        <span>vyriešených</span>
      </div>
    </div>

    <div class="feedback-filters">
      <label class="filter-field feedback-search">
        <span>Hľadať</span>
        <input
          v-model.trim="search"
          type="search"
          placeholder="Popis, používateľ, URL alebo ID hypotéky"
        >
      </label>
      <label class="filter-field">
        <span>Stav</span>
        <select v-model="selectedStatus">
          <option value="all">Všetky stavy</option>
          <option v-for="status in statuses" :key="status.value" :value="status.value">
            {{ status.label }}
          </option>
        </select>
      </label>
    </div>

    <div v-if="loading && !items.length" class="feedback-state">
      <v-progress-circular class="loading-spinner small" indeterminate />
      <span>Načítavam feedback...</span>
    </div>

    <div v-else-if="loadError && !items.length" class="feedback-state feedback-state--error">
      <strong>Feedback sa nepodarilo načítať.</strong>
      <span>Skontrolujte nasadenie API a skúste to znova.</span>
      <button class="secondary-button" type="button" @click="loadItems">Skúsiť znova</button>
    </div>

    <div v-else-if="!filteredItems.length" class="feedback-state">
      <strong>{{ items.length ? "Filtru nezodpovedá žiadne hlásenie." : "Zatiaľ neprišiel žiadny feedback." }}</strong>
      <span v-if="items.length">Zmeňte vyhľadávanie alebo vybraný stav.</span>
    </div>

    <section v-else class="feedback-list" aria-label="Hlásenia používateľov">
      <article v-for="item in filteredItems" :key="item.id" class="feedback-item">
        <header class="feedback-item-heading">
          <div class="feedback-author">
            <span class="feedback-id">#{{ item.id }}</span>
            <div>
              <strong>{{ item.reporter_name || "Neznámy používateľ" }}</strong>
              <span>{{ formatDate(item.created) }}</span>
            </div>
          </div>

          <label class="status-control" :class="statusClass(item.status)">
            <span>Stav</span>
            <select
              :value="item.status"
              :disabled="isSaving(item.id)"
              @change="updateStatus(item, $event.target.value)"
            >
              <option v-for="status in statuses" :key="status.value" :value="status.value">
                {{ status.label }}
              </option>
            </select>
          </label>
        </header>

        <p class="feedback-message">{{ item.message }}</p>

        <div class="feedback-context">
          <a :href="item.page_url" target="_blank" rel="noopener noreferrer" :title="item.page_url">
            <span>Stránka</span>
            <strong>{{ displayUrl(item.page_url) }}</strong>
          </a>
          <router-link v-if="item.mortgage" :to="`/karta-hypoteky/${item.mortgage}/detail`">
            <span>Hypotéka</span>
            <strong>#{{ item.mortgage }}</strong>
          </router-link>
          <div v-if="item.client">
            <span>Klient</span>
            <strong>#{{ item.client }}</strong>
          </div>
          <div v-if="item.route_name">
            <span>Obrazovka</span>
            <strong>{{ item.route_name }}</strong>
          </div>
        </div>

        <div v-if="item.screenshot" class="feedback-screenshot">
          <a :href="item.screenshot" target="_blank" rel="noopener noreferrer">
            <img :src="item.screenshot" :alt="`Screenshot k feedbacku #${item.id}`">
            <span>Otvoriť screenshot v plnej veľkosti</span>
          </a>
        </div>

        <details v-if="hasContext(item.context)" class="technical-context">
          <summary>Technický kontext</summary>
          <pre>{{ formatContext(item.context) }}</pre>
        </details>
      </article>
    </section>

    <button
      v-if="items.length < total"
      class="load-more"
      type="button"
      :disabled="loadingMore"
      @click="loadMore"
    >
      {{ loadingMore ? "Načítavam..." : `Načítať ďalšie (${total - items.length})` }}
    </button>
  </main>
</template>

<script>
import {getUserFeedback, patchUserFeedback} from "@/services/brokerService";

const PAGE_SIZE = 100;

export default {
  name: "AdminFeedbackSettings",
  data: () => ({
    items: [],
    total: 0,
    page: 1,
    loading: false,
    loadingMore: false,
    loadError: false,
    search: "",
    selectedStatus: "all",
    savingIds: [],
    statuses: [
      {value: "new", label: "Nové"},
      {value: "in_progress", label: "Rieši sa"},
      {value: "resolved", label: "Vyriešené"},
    ],
  }),
  computed: {
    filteredItems () {
      const query = this.normalize(this.search);
      return this.items.filter((item) => {
        if (this.selectedStatus !== "all" && item.status !== this.selectedStatus) return false;
        if (!query) return true;
        return this.normalize([
          item.id,
          item.reporter_name,
          item.message,
          item.page_url,
          item.route_name,
          item.mortgage,
          item.client,
        ].join(" ")).includes(query);
      });
    },
  },
  created () {
    this.loadItems();
  },
  methods: {
    normalize (value) {
      return String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    },
    async loadItems () {
      if (this.loading) return;
      this.loading = true;
      this.loadError = false;
      try {
        const response = await getUserFeedback({page: 1, page_size: PAGE_SIZE});
        this.items = Array.isArray(response) ? response : response.results || [];
        this.total = Array.isArray(response) ? response.length : response.count || 0;
        this.page = 1;
      } catch (error) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    async loadMore () {
      if (this.loadingMore || this.items.length >= this.total) return;
      this.loadingMore = true;
      try {
        const nextPage = this.page + 1;
        const response = await getUserFeedback({page: nextPage, page_size: PAGE_SIZE});
        const nextItems = Array.isArray(response) ? response : response.results || [];
        this.items = [...this.items, ...nextItems];
        this.total = Array.isArray(response) ? this.items.length : response.count || this.total;
        this.page = nextPage;
      } finally {
        this.loadingMore = false;
      }
    },
    async updateStatus (item, value) {
      if (value === item.status || this.isSaving(item.id)) return;
      this.savingIds.push(item.id);
      try {
        const updated = await patchUserFeedback(item.id, {status: value});
        Object.assign(item, updated);
        this.$store.commit("appState/setSuccessMessage", {
          text: `Feedback #${item.id}: stav bol zmenený na „${updated.status_label}“.` ,
          type: "success",
        });
      } catch (error) {
        this.$store.commit("appState/setSuccessMessage", {
          text: "Stav feedbacku sa nepodarilo zmeniť.",
          type: "error",
        });
      } finally {
        this.savingIds = this.savingIds.filter((id) => id !== item.id);
      }
    },
    isSaving (id) {
      return this.savingIds.includes(id);
    },
    statusCount (status) {
      return this.items.filter((item) => item.status === status).length;
    },
    statusClass (status) {
      return `status-control--${status}`;
    },
    formatDate (value) {
      if (!value) return "";
      return new Intl.DateTimeFormat("sk-SK", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value));
    },
    displayUrl (value) {
      try {
        const url = new URL(value);
        return `${url.host}${url.pathname}${url.search}`;
      } catch (error) {
        return value;
      }
    },
    hasContext (context) {
      return context && typeof context === "object" && Object.keys(context).length > 0;
    },
    formatContext (context) {
      return JSON.stringify(context, null, 2);
    },
  },
};
</script>

<style scoped lang="scss">
.feedback-settings {
  min-width: 0;
}

.feedback-heading,
.feedback-item-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.feedback-heading h2 {
  margin: 3px 0 8px;
}

.feedback-heading p {
  margin: 0;
  color: var(--app-text-muted);
}

.secondary-button,
.load-more {
  min-height: 42px;
  padding: 0 18px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  background: var(--app-surface-muted);
  color: var(--app-primary);
  font-weight: 700;
}

.feedback-stats {
  display: grid;
  margin: 26px 0;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.stat-card {
  display: flex;
  min-height: 88px;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  border: 1px solid var(--app-border);
  border-radius: 14px;
  background: var(--app-surface-muted);
}

.stat-card strong {
  color: var(--app-text);
  font-size: 25px;
}

.stat-card span {
  color: var(--app-text-muted);
  font-size: 12px;
}

.stat-card--new strong { color: #62b4ff; }
.stat-card--progress strong { color: #f1bb5c; }
.stat-card--resolved strong { color: #58d5a3; }

.feedback-filters {
  display: grid;
  margin-bottom: 20px;
  grid-template-columns: minmax(0, 1fr) minmax(180px, .3fr);
  gap: 14px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.filter-field > span {
  color: var(--app-text-muted);
  font-size: 11px;
  font-weight: 700;
}

.filter-field input,
.filter-field select,
.status-control select {
  width: 100%;
  min-height: 42px;
  padding: 0 13px;
  border: 1px solid var(--app-border);
  border-radius: 11px;
  outline: none;
  background: var(--app-surface-muted);
  color: var(--app-text);
}

.filter-field input:focus,
.filter-field select:focus,
.status-control select:focus {
  border-color: var(--app-primary);
}

.feedback-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.feedback-item {
  padding: 20px;
  border: 1px solid var(--app-border);
  border-radius: 16px;
  background: var(--app-surface-muted);
}

.feedback-author {
  display: flex;
  align-items: center;
  gap: 11px;
}

.feedback-author > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.feedback-author span {
  color: var(--app-text-muted);
  font-size: 11px;
}

.feedback-id {
  display: grid;
  min-width: 44px;
  height: 36px;
  padding: 0 8px;
  place-items: center;
  border-radius: 10px;
  background: rgba(98, 180, 255, .13);
  color: var(--app-primary) !important;
  font-weight: 800;
}

.status-control {
  display: flex;
  min-width: 150px;
  flex-direction: column;
  gap: 4px;
}

.status-control > span {
  color: var(--app-text-muted);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
}

.status-control--new select { border-color: rgba(98, 180, 255, .55); }
.status-control--in_progress select { border-color: rgba(241, 187, 92, .55); }
.status-control--resolved select { border-color: rgba(88, 213, 163, .55); }

.feedback-message {
  margin: 18px 0;
  color: var(--app-text);
  font-size: 15px;
  line-height: 1.6;
  white-space: pre-wrap;
}

.feedback-context {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.feedback-context > * {
  display: flex;
  min-width: 0;
  padding: 8px 10px;
  flex-direction: column;
  gap: 2px;
  border: 1px solid var(--app-border);
  border-radius: 10px;
  color: var(--app-text);
  text-decoration: none;
}

.feedback-context > a:first-child {
  max-width: 100%;
  flex: 1 1 320px;
}

.feedback-context span {
  color: var(--app-text-muted);
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
}

.feedback-context strong {
  overflow: hidden;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.feedback-context a:hover {
  border-color: var(--app-primary);
  color: var(--app-primary);
}

.feedback-screenshot {
  margin-top: 14px;
}

.feedback-screenshot a {
  display: inline-flex;
  max-width: 360px;
  flex-direction: column;
  gap: 7px;
  color: var(--app-primary);
  font-size: 11px;
  font-weight: 700;
  text-decoration: none;
}

.feedback-screenshot img {
  width: 100%;
  max-height: 190px;
  border: 1px solid var(--app-border);
  border-radius: 12px;
  object-fit: cover;
  object-position: top;
}

.technical-context {
  margin-top: 14px;
  color: var(--app-text-muted);
  font-size: 11px;
}

.technical-context summary {
  cursor: pointer;
  color: var(--app-primary);
  font-weight: 700;
}

.technical-context pre {
  overflow: auto;
  max-height: 260px;
  margin: 10px 0 0;
  padding: 13px;
  border-radius: 10px;
  background: var(--app-background);
  color: var(--app-text-muted);
  font-size: 10px;
  white-space: pre-wrap;
}

.feedback-state {
  display: flex;
  min-height: 180px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  border: 1px dashed var(--app-border);
  border-radius: 16px;
  color: var(--app-text-muted);
  text-align: center;
}

.feedback-state--error strong { color: #ff7886; }

.load-more {
  display: block;
  margin: 18px auto 0;
}

button:disabled,
select:disabled {
  cursor: wait;
  opacity: .6;
}

@media (max-width: 900px) {
  .feedback-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .feedback-filters { grid-template-columns: 1fr; }
}

@media (max-width: 600px) {
  .feedback-heading,
  .feedback-item-heading { flex-direction: column; }
  .status-control { width: 100%; }
  .feedback-stats { grid-template-columns: 1fr 1fr; }
}
</style>
